import { IFormAppFieldDTO, IFormApplicationDTO, IfileValue } from 'shared/interfaces/formApplication';
import { FormFieldDTO } from 'shared/interfaces/form';
import { applicationsService } from 'api/applications.service';

console.log('🟢 newApplicationHelpers.ts VERSION: 2024-01-18-DEBUG loaded');

// Re-export the interface for backward compatibility
export interface IFileValue extends IfileValue {}

/**
 * Remove expired temporary files from formValues before saving
 * This prevents race conditions where backend cleanup deletes files
 * but frontend hasn't detected the expiration yet
 */
const removeExpiredFiles = (formValues: { [key: string]: any }): { [key: string]: any } => {
    const now = new Date();
    const cleanedValues = { ...formValues };

    Object.keys(cleanedValues).forEach((key) => {
        const value = cleanedValues[key];

        // Check if this field contains files
        if (Array.isArray(value) && value.length > 0 && value[0]?.fileName) {
            // Filter out expired temporary files
            const validFiles = value.filter((file: IFileValue) => {
                if (file.isTemporary && file.expiresAt) {
                    const expirationDate = new Date(file.expiresAt);
                    const isValid = expirationDate > now;

                    if (!isValid) {
                        console.warn(`🗑️ Filtering out expired file before save: ${file.fileName}`);
                    }

                    return isValid;
                }
                // Non-temporary files are always valid
                return true;
            });

            cleanedValues[key] = validFiles;
        }
    });

    return cleanedValues;
};

export const buildInitialFormValues = (application: IFormApplicationDTO) => {
    return application.applicationFields.reduce((acc: { [key: string]: any }, field: IFormAppFieldDTO) => {
        // For checkbox, handle both true and false as valid saved values
        if (field.type === 'checkbox') {
            // Convert string to boolean if needed (MongoDB might store as string)
            let checkboxValue: boolean;

            if (field.value === true || field.value === 'true' || field.value === 1) {
                checkboxValue = true;
            } else if (field.value === false || field.value === 'false' || field.value === 0) {
                checkboxValue = false;
            } else if (field.value === null || field.value === undefined || field.value === '') {
                // No saved value - use default or false
                checkboxValue = field.defaultValues?.defaultBoolean === true ? true : false;
            } else {
                // Fallback
                checkboxValue = false;
            }

            acc[field.fieldIndex] = checkboxValue;
            return acc;
        }

        // For other types, check if there's a saved value
        if (field.value != null && field.value !== '') {
            // Keep 0-based index for select fields (DB stores 0-based, CustomAutocomplete uses 0-based)
            acc[field.fieldIndex] = field.value;
            return acc;
        }

        // No saved value - use defaults
        switch (field.type) {
            case 'select': {
                // Select fields always have a value - use defaultSelectionIndex or first option (0) as fallback
                const defaultIndex = field.defaultValues?.defaultSelectionIndex;
                const optionsLength = field.options?.length || 0;

                acc[field.fieldIndex] =
                    defaultIndex !== undefined && defaultIndex >= 0 && defaultIndex < optionsLength
                        ? defaultIndex
                        : optionsLength > 0
                        ? 0
                        : 0;
                break;
            }
            case 'text':
            case 'textarea':
            case 'date':
            case 'time':
                acc[field.fieldIndex] = field.defaultValues?.defaultText || '';
                break;
            case 'number':
                acc[field.fieldIndex] = field.defaultValues?.defaultNumber || '';
                break;
            case 'file':
                acc[field.fieldIndex] = field.fileValues || [];
                break;
            default:
                acc[field.fieldIndex] = '';
        }

        return acc;
    }, {});
};

/**
 * The applicant may still edit (save / submit) the application: a draft, or one returned to
 * them for changes. Same rule as the server (ExternalRequestsApi buildExternalUpdate).
 */
export const isEditableStatus = (status?: string): boolean => status === 'draft' || status === 'returned';

export const saveApplication = async (
    application: IFormApplicationDTO,
    formValues: { [key: string]: any }
) => {
    // Clean expired files before saving to prevent race conditions
    const cleanedFormValues = removeExpiredFiles(formValues);

    //Check if mandatory fields filled-out
    const missingMandatoryFields = getMissingMandatoryFields(application.applicationFields, cleanedFormValues);
    if (missingMandatoryFields.length > 0) {
        throw new MandatoryFieldsMissingError(missingMandatoryFields.map((field) => field.label));
    }
    // Update application. Post-#214 the backend builds the approval flow from the
    // form (via formRef) on submit, so the client only marks the submission pending
    // and sends the field values — it no longer carries the form's approvalFlow.
    const updatedApplication: IFormApplicationDTO = {
        ...application,
        appliedTimestamp: application.appliedTimestamp ? application.appliedTimestamp : new Date(), //If not exist (new application), create one.
        lastUpdatedTimestamp: new Date(),
        applicationStatus: 'pending',
        applicationFields: application.applicationFields.map((fieldToUpdate) => {
            // Look up the value by fieldIndex instead of using array index
            // Try both number and string key to handle type conversion
            const fieldValue = cleanedFormValues[fieldToUpdate.fieldIndex] ?? cleanedFormValues[String(fieldToUpdate.fieldIndex)];

            if (fieldToUpdate.type === 'file') {
                return {
                    ...fieldToUpdate,
                    fileValues: (fieldValue as IFileValue[]) || fieldToUpdate.fileValues || [], // Use new value or preserve existing
                };
            } else if (fieldToUpdate.type === 'select') {
                // CustomAutocomplete returns 0-based index, DB stores 0-based index - no conversion needed
                const calculatedValue =
                    fieldValue === '' || fieldValue === null || fieldValue === undefined ? '' : Number(fieldValue);

                return {
                    ...fieldToUpdate,
                    value: calculatedValue, // Store 0-based index in DB
                };
            } else {
                // For other field types, use the new value if available, otherwise preserve existing
                return {
                    ...fieldToUpdate,
                    value: fieldValue !== undefined ? (fieldValue as string | boolean | number) : fieldToUpdate.value,
                };
            }
        }),
    };

    // Errors propagate as-is (status + server message) — see getSaveErrorNotice.
    return applicationsService.update(updatedApplication);
};

export const saveApplicationAsDraft = async (application: IFormApplicationDTO, formValues: { [key: string]: any }) => {
    // Clean expired files before saving to prevent race conditions
    const cleanedFormValues = removeExpiredFiles(formValues);

    //Update draft application
    const updatedDraftApplication: IFormApplicationDTO = {
        ...application,
        appliedTimestamp: application.appliedTimestamp ? application.appliedTimestamp : new Date(), //If not exist (new application), create one.
        lastUpdatedTimestamp: new Date(),
        applicationFields: application.applicationFields.map((fieldToUpdate) => {
            // Look up the value by fieldIndex instead of using array index
            // Try both number and string key to handle type conversion
            const fieldValue = cleanedFormValues[fieldToUpdate.fieldIndex] ?? cleanedFormValues[String(fieldToUpdate.fieldIndex)];

            if (fieldToUpdate.type === 'file') {
                return {
                    ...fieldToUpdate,
                    fileValues: (fieldValue as IFileValue[]) || fieldToUpdate.fileValues || [], // Use new value or preserve existing
                };
            } else if (fieldToUpdate.type === 'select') {
                // CustomAutocomplete returns 0-based index, DB stores 0-based index - no conversion needed
                const calculatedValue =
                    fieldValue === '' || fieldValue === null || fieldValue === undefined ? '' : Number(fieldValue);

                return {
                    ...fieldToUpdate,
                    value: calculatedValue, // Store 0-based index in DB
                };
            } else {
                // For other field types, use the new value if available, otherwise preserve existing
                return {
                    ...fieldToUpdate,
                    value: fieldValue !== undefined ? (fieldValue as string | boolean | number) : fieldToUpdate.value,
                };
            }
        }),
    };

    // Errors propagate as-is (status + server message) — see getSaveErrorNotice.
    return applicationsService.update(updatedDraftApplication);
};

/** Thrown by saveApplication when mandatory fields are empty; carries their labels for the notice. */
export class MandatoryFieldsMissingError extends Error {
    constructor(public readonly missingFields: string[]) {
        super('MANDATORY_FIELDS_MISSING');
    }
}

/** The mandatory fields that are still empty (file: no attachment, checkbox: unchecked, select: no choice). */
const getMissingMandatoryFields = (fields: FormFieldDTO[], formValues: { [key: string]: any }): FormFieldDTO[] => {
    return fields.filter((field) => {
        if (!field.mandatory) return false;
        const value = formValues[field.fieldIndex];

        // Special handling for file fields - check if fileValues array exists and has items
        if (field.type === 'file') {
            // For file fields, value is an array of IFileValue
            if (!value || !Array.isArray(value) || value.length === 0) {
                console.log(
                    `❌ MANDATORY FILE FIELD MISSING: Field "${field.label}" (type: file) is mandatory - file must be attached`
                );
                return true; // Field is missing
            }
            return false; // Field is filled
        }

        // For mandatory checkbox, must be checked (true)
        if (field.type === 'checkbox') {
            if (!value) {
                console.log(
                    `❌ MANDATORY CHECKBOX FIELD MISSING: Field "${field.label}" (type: checkbox) is mandatory - must be checked`
                );
                return true; // Field is not checked
            }
            return false; // Field is checked
        }

        // For select fields, check if value is empty, null, undefined, or -1
        if (field.type === 'select') {
            const selectedIndex = typeof value === 'number' ? value : Number(value);
            if (value === '' || value === null || value === undefined || selectedIndex === -1) {
                console.log(
                    `❌ MANDATORY SELECT FIELD MISSING: Field "${field.label}" (type: select) is mandatory - must select a value`
                );
                return true; // Field is missing
            }
            return false; // Field is filled
        }

        // For all other field types, check if value is empty, null, or undefined
        if (value === undefined || value === null || value === '') {
            console.log(`❌ MANDATORY FIELD MISSING: Field "${field.label}" (type: ${field.type}) is mandatory`);
            return true; // Field is missing
        }

        return false; // Field is filled
    });
};

// Client-side approver resolution removed (#214): the backend now builds the
// approval flow and resolves approvers on submit (ExternalRequestsApi
// buildFirstApprovalStage), so the External Frontend no longer derives decisions.

/** How many missing field names a notice lists before "and N more". */
const MAX_LISTED_FIELDS = 5;

export interface SaveErrorNotice {
    message: string;
    severity: 'warning' | 'error';
    /** The OTP session expired / is invalid → the caller should send the user to the OTP page. */
    sessionExpired?: boolean;
}

/**
 * User-facing notice for a failed save / submit. Problems the user can fix are warnings that
 * name the field (the server replies 400 "Field validation failed" with a message like
 * `Field "<label>" (type: text) is mandatory`); only unexpected failures are errors.
 */
export const getSaveErrorNotice = (error: any, fallbackMessage: string): SaveErrorNotice => {
    if (error?.message === 'MANDATORY_FIELDS_MISSING') {
        const missingFields: string[] = error.missingFields ?? [];
        if (missingFields.length === 1) {
            return { message: `השדה "${missingFields[0]}" הוא שדה חובה.`, severity: 'warning' };
        }
        if (missingFields.length > 1) {
            const listed = missingFields.slice(0, MAX_LISTED_FIELDS).map((label) => `"${label}"`).join(', ');
            const more = missingFields.length > MAX_LISTED_FIELDS ? ` ועוד ${missingFields.length - MAX_LISTED_FIELDS}` : '';
            return { message: `יש למלא את שדות החובה: ${listed}${more}.`, severity: 'warning' };
        }
        return { message: 'שדות המסומנים בכוכבית אדומה לא יכולים להישאר ריקים!', severity: 'warning' };
    }

    const status: number | undefined = error?.response?.status;
    const body = error?.response?.data;

    if (status === 400 && body?.error === 'Field validation failed') {
        const serverMessage: string = body.message || '';
        const fieldLabel = serverMessage.match(/Field "(.+?)"/)?.[1];
        if (!fieldLabel) return { message: 'יש לבדוק את הערכים בטופס.', severity: 'warning' };
        return /is mandatory/.test(serverMessage)
            ? { message: `השדה "${fieldLabel}" הוא שדה חובה.`, severity: 'warning' }
            : { message: `הערך בשדה "${fieldLabel}" אינו תקין.`, severity: 'warning' };
    }
    if (status === 409) {
        return { message: 'לא ניתן לערוך את הבקשה במצבה הנוכחי.', severity: 'warning' };
    }
    if (status === 401 || status === 403) {
        return { message: 'פג תוקף ההזדהות. יש להזדהות מחדש.', severity: 'warning', sessionExpired: true };
    }
    return { message: fallbackMessage, severity: 'error' };
};
